import type { ClipboardEvent, MouseEvent, SyntheticEvent, DragEvent } from 'react';
import type { Options } from 'ahooks/lib/useDrop';

export interface Accept {
  checkReg: RegExp;
  accept: string;
  msg: string;
}

type AcceptArg = Accept | undefined;

type BuiltInAcceptTypes = 'img' | 'doc' | 'pdf' | 'ppt';

const accepts: Record<BuiltInAcceptTypes, Accept> = {
  img: {
    checkReg: /image\/*/,
    accept: '.jpg,.png,.jpeg,.webp,.gif',
    msg: 'Only image files can be uploaded',
  },
  doc: {
    checkReg: /^application\/.*word/,
    accept:
      '.doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    msg: 'Only .doc and .docx files can be uploaded',
  },
  pdf: {
    checkReg: /^application\/.*pdf/,
    accept: '.pdf,application/pdf',
    msg: 'Only .pdf files can be uploaded',
  },
  ppt: {
    checkReg: /^application\/.*(powerpoint|presentationml)/,
    accept:
      '.ppt,.pptx,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.slideshow,application/vnd.openxmlformats-officedocument.presentationml.presentation',
    msg: 'Only .ppt、.pptx files can be uploaded',
  },
};

export interface Props {
  accept?: BuiltInAcceptTypes | Accept;
  multiple?: boolean;
  maxMB?: number;
  maxLength?: number;
  dropRef?: React.RefObject<HTMLDivElement>;
  options?: Options;
}

export function usePickFiles({
  accept,
  multiple = false,
  maxMB = 10 * 1024,
  maxLength = 1,
  dropRef,
  options = {},
}: Props = {}) {
  const [files, setFiles] = useState<File[]>();
  const [error, setError] = useState<Error>();
  const [isDragging, setIsDragging] = useState(false);

  const file = useMemo(() => files?.[0], [files]);

  const accept_ = useMemo(
    () => (typeof accept === 'string' ? accepts[accept] : accept),
    [accept]
  );

  const handler = useCallback(
    (files: File[]) => {
      try {
        check(files, accept_, maxMB, maxLength);
        setFiles(files);
        setError(undefined);
      } catch (err) {
        setFiles(undefined);
        setError(err as Error);
      }
    },
    [accept_, maxMB, maxLength]
  );

  const reset = useCallback(() => {
    setFiles(undefined);
    setError(undefined);
  }, []);

  const message = useMemo(() => {
    if (error) {
      return error.message;
    } else {
      return `${accept_ ? accept_.msg : ''} and the file size cannot exceed${maxMB}MB`;
    }
  }, [error, accept_, maxMB]);

  useDrop(dropRef, {
    onFiles: (files) => {
      setIsDragging(false);
      handler(files);
    },
    onDragEnter: () => setIsDragging(true),
    onDragOver: () => setIsDragging(true),
    onDragLeave: () => setIsDragging(false),
    ...options,
  });

  const on = useMemo(
    () => ({
      onClick(e: MouseEvent) {
        stopDefault(e);
        selectFile(accept_, multiple, (e: any) => {
          handler(e.target.files);
        });
      },
    }),
    [accept_, handler, multiple]
  );

  useEffect(() => {
    reset();
  }, [accept_, maxMB, reset]);

  return {
    isDragging,
    on,
    file,
    files,
    error,
    message,
    reset,
    setFiles,
    setError,
  };
}

export interface GlobalPickFilesOptions {
  accept?: BuiltInAcceptTypes | Accept;
  multiple?: boolean;
  maxMB?: number;
  maxLength?: number;
  dropRef?: React.RefObject<HTMLDivElement>;
  options?: Options;
}

export function useGlobalPickFiles(defaultOptions?: GlobalPickFilesOptions) {
  const [files, setFiles] = useState<File[]>();
  const [error, setError] = useState<Error>();
  const [isDragging, setIsDragging] = useState(false);

  const file = useMemo(() => files?.[0], [files]);

  const getOptions = useCallback(
    (options?: GlobalPickFilesOptions) => {
      const options_ = {
        multiple: true,
        maxMB: 1024, // 1GB
        maxLength: 100,
        ...defaultOptions,
        ...options,
      };

      const { accept } = options_;

      return Object.assign(options_, {
        accept: typeof accept === 'string' ? accepts[accept] : accept,
      });
    },
    [defaultOptions]
  );

  const handler = useCallback(
    (files: File[], options?: GlobalPickFilesOptions) => {
      const { accept, maxMB, maxLength } = getOptions(options);
      try {
        check(files, accept, maxMB, maxLength);
        setFiles(files);
        setError(undefined);
      } catch (err) {
        setFiles(undefined);
        setError(err as Error);
        throw err;
      }
    },
    [getOptions]
  );

  const reset = useCallback(() => {
    setFiles(undefined);
    setError(undefined);
  }, []);

  const pick = useCallback(
    (options?: GlobalPickFilesOptions) => {
      const { accept, multiple } = getOptions(options);
      selectFile(accept, multiple, (e: any) => {
        handler(Array.from(e.target.files), options);
      });
    },
    [getOptions, handler]
  );

  const getEventHandlers = useCallback(
    (options?: GlobalPickFilesOptions) => {
      return {
        click: {
          onClick(e?: MouseEvent) {
            stopDefault(e);
            pick(options);
          },
        },
        drop: {
          onDragEnter(e: DragEvent) {
            stopDefault(e);
            setIsDragging(true);
          },
          onDragOver(e: DragEvent) {
            stopDefault(e);
            setIsDragging(true);
          },
          onDragLeave(e: DragEvent) {
            stopDefault(e);
            setIsDragging(false);
          },
          onDrop(e: DragEvent) {
            stopDefault(e);
            setIsDragging(false);
            if (e.dataTransfer?.files?.length) {
              handler(Array.from(e.dataTransfer.files), options);
            }
          },
        },
        paste: {
          onPaste(e: ClipboardEvent) {
            stopDefault(e);
            if (e.clipboardData?.files?.length) {
              handler(Array.from(e.clipboardData.files), options);
            }
          },
        },
      };
    },
    [handler, pick]
  );

  useEffect(() => {
    reset();
  }, [reset]);

  return useMemo(
    () => ({
      isDragging,
      pick,
      getEventHandlers,
      file,
      files,
      error,
      reset,
      setFiles,
      setError,
    }),
    [error, file, files, getEventHandlers, isDragging, pick, reset]
  );
}

function stopDefault(e?: SyntheticEvent | Event) {
  if (e) {
    e.preventDefault();
    e.stopPropagation();
  }
}

function selectFile(accept: AcceptArg, multiple: boolean, cb: (e: Event) => void) {
  const fileInputDom = document.createElement('input');
  fileInputDom.type = 'file';
  fileInputDom.multiple = multiple;
  fileInputDom.addEventListener('change', handler, false);
  fileInputDom.accept = accept ? accept.accept : '';
  fileInputDom.click();
  function handler(e: Event) {
    cb(e);
    fileInputDom.removeEventListener('change', handler, false);
  }
}

function check(files: File[], accept: AcceptArg, maxMB: number, maxLength: number) {
  checkLength(files, maxLength);
  for (const file of files) {
    checkSize(file, maxMB);
    checkType(file, accept);
  }
  return files;
}

function checkLength(files: File[], max: number) {
  if (files.length) {
    if (files.length > max) {
      throw new Error(`You can only select a maximum of ${max} files`, {
        cause: {
          type: 'multiple',
        },
      });
    }
  } else {
    throw new Error('Please select a file', {
      cause: {
        type: 'empty',
      },
    });
  }
}

function checkSize(file: File, maxMB: number) {
  if (file.size > maxMB * 1024 * 1024) {
    throw new Error(`Oops! ${file.name} is too large.`, {
      cause: {
        type: 'size',
      },
    });
  }
}

function checkType(file: File, accept: AcceptArg) {
  if (accept && !accept.checkReg.test(file.type)) {
    throw new Error(accept.msg, {
      cause: {
        type: 'accept',
        accept,
      },
    });
  }
}
